import * as API from '../api.js';
import {
    TOAST_DEFAULT_DURATION,
    TOAST_INLINE_STATUS_DURATION_MS,
    TOAST_INLINE_STATUS_MAX_CHARS,
    TOAST_PHONE_SHORT_EDGE_MAX_PX,
    TOAST_PRIORITY_DEFAULT,
    TOAST_PRIORITY_LEVELS,
    TOAST_SUPPRESSED_ERROR_MODE,
    TOAST_SUPPRESSION_BYPASS_PRIORITIES,
    TOAST_SUPPRESS_DURING_ONBOARDING_ON_PHONE,
} from '../config/toasts.js';

const TOAST_MOBILE_UA_RE = /android|iphone|ipod|iemobile|opera mini|mobile/i;
const TOAST_MOBILE_TABLET_UA_RE = /ipad|tablet/i;
const TOAST_ERROR_PRIORITY_RE = /\b(error|fail|failed|failure|unavailable|blocked|denied|invalid|missing|timeout)\b/i;
const TOAST_WARN_PRIORITY_RE = /\b(warning|warn|quota|limit|paused|retry|cancelled|canceled)\b/i;
const TOAST_VIEWPORT_SIDE_GAP_PX = 16;
const TOAST_PRIORITY_SET = new Set(
    (Array.isArray(TOAST_PRIORITY_LEVELS) ? TOAST_PRIORITY_LEVELS : [])
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
);
const TOAST_BYPASS_PRIORITY_SET = new Set(
    (Array.isArray(TOAST_SUPPRESSION_BYPASS_PRIORITIES) ? TOAST_SUPPRESSION_BYPASS_PRIORITIES : [])
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
);

const _runtime = {
    initialized: false,
    bodyObserver: null,
    suppressionActive: false,
    inlineStatusTimer: 0,
    suppressedCounts: {
        info: 0,
        warn: 0,
        error: 0,
        inline: 0,
    },
};

function createNoopToastController() {
    return {
        element: null,
        updateMessage: () => {},
        close: () => {},
    };
}

function normalizeToastText(value) {
    // Keep UX-friendly line breaks while stripping markup.
    return String(value || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '');
}

function compactSingleLine(value) {
    return normalizeToastText(value)
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizePriority(value, fallback = '') {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return fallback;
    if (TOAST_PRIORITY_SET.has(normalized)) return normalized;
    return fallback;
}

function inferPriority(title, message) {
    const haystack = `${normalizeToastText(title)} ${normalizeToastText(message)}`.trim();
    if (!haystack) return TOAST_PRIORITY_DEFAULT;
    if (TOAST_ERROR_PRIORITY_RE.test(haystack)) return 'error';
    if (TOAST_WARN_PRIORITY_RE.test(haystack)) return 'warn';
    return TOAST_PRIORITY_DEFAULT;
}

function parseToastOptions(title, message, msOrOptions, maybeOptions) {
    const parsed = {
        duration: TOAST_DEFAULT_DURATION,
        priority: '',
        bypassSuppression: false,
    };

    const apply = (value) => {
        if (value == null) return;
        if (typeof value === 'number') {
            parsed.duration = value;
            return;
        }
        if (typeof value === 'string') {
            const asNumber = Number(value);
            if (Number.isFinite(asNumber) && value.trim() !== '') {
                parsed.duration = asNumber;
            } else {
                parsed.priority = value;
            }
            return;
        }
        if (typeof value !== 'object') return;
        if (value.duration != null) parsed.duration = value.duration;
        if (value.ms != null) parsed.duration = value.ms;
        if (value.priority != null) parsed.priority = value.priority;
        if (value.level != null) parsed.priority = value.level;
        if (value.bypassSuppression != null) parsed.bypassSuppression = value.bypassSuppression;
        if (value.forceGlobal != null) parsed.bypassSuppression = value.forceGlobal;
        if (value.force != null) parsed.bypassSuppression = value.force;
    };

    apply(msOrOptions);
    apply(maybeOptions);

    const durationNum = Number(parsed.duration);
    const duration = Number.isFinite(durationNum) ? Math.max(0, Math.round(durationNum)) : TOAST_DEFAULT_DURATION;
    const fallbackPriority = normalizePriority(TOAST_PRIORITY_DEFAULT, 'info') || 'info';
    const normalizedPriority = normalizePriority(parsed.priority, '')
        || normalizePriority(inferPriority(title, message), fallbackPriority)
        || fallbackPriority;
    const bypassSuppression = Boolean(parsed.bypassSuppression);
    return {
        duration,
        priority: normalizedPriority,
        bypassSuppression,
    };
}

function matchesMediaQuery(query) {
    if (!query || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    try {
        return Boolean(window.matchMedia(query).matches);
    } catch (_e) {
        return false;
    }
}

function shortEdgePx() {
    if (typeof window === 'undefined') return 0;
    const w = Math.max(0, Number(window.innerWidth || 0));
    const h = Math.max(0, Number(window.innerHeight || 0));
    if (!w || !h) return 0;
    return Math.min(w, h);
}

function userAgentHintsPhone() {
    if (typeof navigator === 'undefined') return false;
    const ua = String(navigator.userAgent || '').toLowerCase();
    if (!ua) return false;
    if (TOAST_MOBILE_TABLET_UA_RE.test(ua)) return false;
    return TOAST_MOBILE_UA_RE.test(ua);
}

function suppressionContext() {
    const onboardingActive = Boolean(
        typeof document !== 'undefined' &&
        document.body &&
        document.body.classList.contains('onboarding-active')
    );
    const shortEdge = shortEdgePx();
    const coarsePointer = matchesMediaQuery('(pointer: coarse)') || matchesMediaQuery('(hover: none)');
    const compactViewport = shortEdge > 0 && shortEdge <= Number(TOAST_PHONE_SHORT_EDGE_MAX_PX || 0);
    const uaPhone = userAgentHintsPhone();
    const phoneContext = Boolean(uaPhone || (coarsePointer && compactViewport));
    const suppressed = Boolean(
        TOAST_SUPPRESS_DURING_ONBOARDING_ON_PHONE &&
        onboardingActive &&
        phoneContext
    );
    return {
        suppressed,
        onboarding_active: onboardingActive,
        phone_context: phoneContext,
        coarse_pointer: coarsePointer,
        ua_phone: uaPhone,
        short_edge_px: Math.round(shortEdge),
    };
}

function clearToastContainer() {
    const cont = document.getElementById('toastContainer');
    if (!cont) return 0;
    const count = cont.childElementCount || 0;
    while (cont.firstChild) {
        cont.removeChild(cont.firstChild);
    }
    return count;
}

function computeDynamicToastMaxWidthPx() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
    const viewportWidth = Math.max(0, Number(window.innerWidth || document.documentElement?.clientWidth || 0));
    if (viewportWidth <= 0) return 0;

    let maxWidthPx = viewportWidth - (TOAST_VIEWPORT_SIDE_GAP_PX * 2);
    const workflowLayout = document.querySelector('.workflow-layout');
    if (workflowLayout && typeof workflowLayout.getBoundingClientRect === 'function') {
        const rect = workflowLayout.getBoundingClientRect();
        const workflowRightEdge = Number(rect.right);
        if (Number.isFinite(workflowRightEdge)) {
            const rightGutter = viewportWidth - workflowRightEdge;
            // Keep equal side padding inside the right gutter so toast never crosses into workflow area.
            const constrained = rightGutter - (TOAST_VIEWPORT_SIDE_GAP_PX * 2);
            if (Number.isFinite(constrained) && constrained > 120) {
                maxWidthPx = Math.min(maxWidthPx, constrained);
            }
        }
    }
    if (!Number.isFinite(maxWidthPx) || maxWidthPx <= 0) return 0;
    return Math.floor(maxWidthPx);
}

function applyDynamicToastLayout(cont = null) {
    if (typeof document === 'undefined') return;
    const container = cont || document.getElementById('toastContainer');
    if (!container) return;
    const maxWidthPx = computeDynamicToastMaxWidthPx();
    if (maxWidthPx > 0) {
        container.style.setProperty('--toast-dynamic-max-width', `${maxWidthPx}px`);
    } else {
        container.style.removeProperty('--toast-dynamic-max-width');
    }
}

function flushSuppressedCounts(reason = 'flush') {
    const counts = _runtime.suppressedCounts || {};
    const total = Number(counts.info || 0) + Number(counts.warn || 0) + Number(counts.error || 0);
    if (total <= 0) return;
    API.logUiFlow('toast', 'onboarding_mobile_suppressed_flush', 'ok', {
        reason,
        count_total: total,
        count_info: Number(counts.info || 0),
        count_warn: Number(counts.warn || 0),
        count_error: Number(counts.error || 0),
        count_inline: Number(counts.inline || 0),
    }).catch(() => {});
    _runtime.suppressedCounts = { info: 0, warn: 0, error: 0, inline: 0 };
}

export function clearOnboardingInlineStatus() {
    if (_runtime.inlineStatusTimer) {
        if (typeof window !== 'undefined') {
            window.clearTimeout(_runtime.inlineStatusTimer);
        }
        _runtime.inlineStatusTimer = 0;
    }
    const row = document.getElementById('onboardingInlineStatus');
    if (!row) return;
    row.classList.add('hidden');
    row.textContent = '';
    row.title = '';
    row.removeAttribute('data-priority');
}

function syncSuppressionState(ctx, trigger = 'runtime') {
    const next = Boolean(ctx && ctx.suppressed);
    const prev = Boolean(_runtime.suppressionActive);
    if (next === prev) return;
    _runtime.suppressionActive = next;
    if (next) {
        const cleared = clearToastContainer();
        API.logUiFlow('toast', 'onboarding_mobile_suppression', 'enabled', {
            trigger,
            cleared_toasts: cleared,
            ...ctx,
        }).catch(() => {});
        return;
    }
    flushSuppressedCounts('suppression_disabled');
    clearOnboardingInlineStatus();
    API.logUiFlow('toast', 'onboarding_mobile_suppression', 'disabled', {
        trigger,
        ...ctx,
    }).catch(() => {});
}

function ensureSuppressionObservers() {
    if (_runtime.initialized) return;
    _runtime.initialized = true;

    const recompute = (trigger = 'observer') => {
        applyDynamicToastLayout();
        syncSuppressionState(suppressionContext(), trigger);
    };

    if (typeof window !== 'undefined') {
        window.addEventListener('resize', () => recompute('resize'), { passive: true });
        window.addEventListener('orientationchange', () => recompute('orientation'), { passive: true });
    }
    if (typeof document !== 'undefined') {
        const bindBodyObserver = () => {
            if (_runtime.bodyObserver || !document.body || typeof MutationObserver !== 'function') return;
            _runtime.bodyObserver = new MutationObserver(() => recompute('body_class'));
            _runtime.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        };
        if (document.body) {
            bindBodyObserver();
        } else {
            document.addEventListener('DOMContentLoaded', bindBodyObserver, { once: true });
        }
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) return;
            recompute('visibility');
        });
    }
    recompute('init');
}

function upsertOnboardingInlineStatus({ priority, title, message }) {
    const coachmark = document.getElementById('onboardingCoachmark');
    if (!coachmark || coachmark.classList.contains('hidden')) return false;
    let row = document.getElementById('onboardingInlineStatus');
    if (!row) {
        row = document.createElement('div');
        row.id = 'onboardingInlineStatus';
        row.className = 'onboarding-inline-status hidden';
        row.setAttribute('role', 'status');
        row.setAttribute('aria-live', 'polite');
        coachmark.appendChild(row);
    }
    const normalizedTitle = compactSingleLine(title);
    const normalizedMessage = compactSingleLine(message);
    const joined = (priority === 'error' && normalizedTitle && normalizedMessage)
        ? `${normalizedTitle}: ${normalizedMessage}`
        : (normalizedTitle || normalizedMessage);
    const safeMax = Math.max(40, Number(TOAST_INLINE_STATUS_MAX_CHARS || 0));
    const compact = String(joined || '').trim();
    const text = compact.length > safeMax ? `${compact.slice(0, safeMax - 3)}...` : compact;
    if (!text) return false;
    row.dataset.priority = priority || TOAST_PRIORITY_DEFAULT;
    row.textContent = text;
    row.title = compact;
    row.classList.remove('hidden');

    if (_runtime.inlineStatusTimer) {
        if (typeof window !== 'undefined') {
            window.clearTimeout(_runtime.inlineStatusTimer);
        }
        _runtime.inlineStatusTimer = 0;
    }
    const durationMs = Math.max(0, Number(TOAST_INLINE_STATUS_DURATION_MS || 0));
    if (durationMs > 0) {
        _runtime.inlineStatusTimer = window.setTimeout(() => {
            const liveRow = document.getElementById('onboardingInlineStatus');
            if (!liveRow) return;
            liveRow.classList.add('hidden');
            liveRow.textContent = '';
            liveRow.title = '';
            liveRow.removeAttribute('data-priority');
            _runtime.inlineStatusTimer = 0;
        }, durationMs);
    }
    return true;
}

function recordSuppressed(priority, route = 'drop', ctx = {}) {
    const safePriority = normalizePriority(priority, 'info');
    if (!_runtime.suppressedCounts[safePriority]) {
        _runtime.suppressedCounts[safePriority] = 0;
    }
    _runtime.suppressedCounts[safePriority] += 1;
    if (route === 'inline') {
        _runtime.suppressedCounts.inline += 1;
    }
    if (safePriority === 'error') {
        API.logUiFlow('toast', 'suppressed_critical', route, {
            priority: safePriority,
            ...ctx,
        }).catch(() => {});
    }
}

export function showToast(title, message = "", msOrOptions = TOAST_DEFAULT_DURATION, maybeOptions = undefined) {
    ensureSuppressionObservers();
    const cfg = parseToastOptions(title, message, msOrOptions, maybeOptions);
    const ctx = suppressionContext();
    syncSuppressionState(ctx, 'show_toast');

    if (ctx.suppressed) {
        const isBypassPriority = TOAST_BYPASS_PRIORITY_SET.has(cfg.priority);
        const allowPriorityBypass = isBypassPriority && TOAST_SUPPRESSED_ERROR_MODE === 'bypass';
        const shouldBypassSuppression = cfg.bypassSuppression || allowPriorityBypass;
        if (!shouldBypassSuppression) {
            const inlineShown = upsertOnboardingInlineStatus({
                priority: cfg.priority,
                title,
                message,
            });
            const route = inlineShown ? 'inline' : 'drop';
            recordSuppressed(cfg.priority, route, ctx);
            return createNoopToastController();
        }
    }
    if (!ctx.suppressed) {
        clearOnboardingInlineStatus();
    }

    const cont = document.getElementById('toastContainer');
    if (!cont) return createNoopToastController();
    applyDynamicToastLayout(cont);

    const el = document.createElement('div');
    el.className = "toast";
    el.dataset.priority = cfg.priority;
    const row = document.createElement('div');
    row.className = 'toast-row';

    const content = document.createElement('div');
    content.className = 'toast-content';

    const strong = document.createElement('strong');
    strong.textContent = normalizeToastText(title);
    content.appendChild(strong);

    const body = document.createElement('div');
    body.className = 'toast-body-content';
    body.textContent = normalizeToastText(message);
    content.appendChild(body);

    const closeBtn = document.createElement('div');
    closeBtn.className = 'toast-close';
    closeBtn.textContent = '✕';

    row.appendChild(content);
    row.appendChild(closeBtn);
    el.appendChild(row);

    // Define close function
    const closeToast = () => {
        try { el.remove(); } catch(e) {}
    };

    if (closeBtn) {
        closeBtn.onclick = closeToast;
    }

    cont.appendChild(el);

    if (cfg.duration > 0) {
        setTimeout(closeToast, cfg.duration);
    }

    // --- CONTROLLER INTERFACE ---
    return {
        element: el,
        updateMessage: (newMsg) => {
            const body = el.querySelector('.toast-body-content');
            if (body) body.textContent = normalizeToastText(newMsg);
        },
        close: closeToast
    };
}
